// Timber Collection — deep hierarchy data (placeholders)
import { IMG, ASSETS } from "@/lib/site";

const LOREM_LEAD =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua — a considered material for interiors that reward the eye up close.";
const LOREM_BODY =
  "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.";

const WOOD_POOL = [
  ASSETS.slatWall, IMG.beamsShadow, IMG.woodBoard1, IMG.woodBoard2, IMG.woodBoard3,
  IMG.woodBoard4, ASSETS.veneerShelves, IMG.herring1, IMG.parquet, IMG.herring2,
  IMG.herring3, IMG.curvedBeams, IMG.timberGeo, IMG.concreteWood,
];

// 30 wood-tone swatches (name + hex) — placeholders
export const WOOD_TONES = [
  { name: "Natural", hex: "#c9a97e" }, { name: "Honey", hex: "#c69a63" },
  { name: "Wheat", hex: "#d8bd93" }, { name: "Sand", hex: "#cbb189" },
  { name: "Chalk", hex: "#e4d8c4" }, { name: "Dune", hex: "#b89a6e" },
  { name: "Fawn", hex: "#b58e5f" }, { name: "Amber", hex: "#a9793f" },
  { name: "Chestnut", hex: "#8a5a34" }, { name: "Cognac", hex: "#94551f" },
  { name: "Walnut", hex: "#6f4a2f" }, { name: "Cocoa", hex: "#5b3d29" },
  { name: "Espresso", hex: "#43301f" }, { name: "Smoked", hex: "#4f3f31" },
  { name: "Graphite", hex: "#3a352f" }, { name: "Ash Grey", hex: "#9b958a" },
  { name: "Pewter", hex: "#8a867c" }, { name: "Driftwood", hex: "#a79f8f" },
  { name: "Fog", hex: "#c2bcae" }, { name: "Linen", hex: "#d9cfba" },
  { name: "Clay", hex: "#b58a6a" }, { name: "Terra", hex: "#a56a45" },
  { name: "Rust", hex: "#8f4e2c" }, { name: "Umber", hex: "#6b4326" },
  { name: "Slate", hex: "#5c5850" }, { name: "Storm", hex: "#726d63" },
  { name: "Mineral", hex: "#867f70" }, { name: "Bone", hex: "#e8ddc8" },
  { name: "Oat", hex: "#d2c3a3" }, { name: "Toast", hex: "#b58b55" },
];

const pick = (n, offset = 0) => {
  const out = [];
  for (let i = 0; i < n; i++) out.push(WOOD_TONES[(i + offset) % WOOD_TONES.length]);
  return out;
};

// ---- Timber hub ----
export const TIMBER_HUB = {
  eyebrow: "Our Collection / 01",
  title: "Timber Collection",
  hero: ASSETS.slatWall,
  intro:
    "Warm, tactile and enduring. Our timber collection spans architectural wall panels, engineered flooring and fine veneers — each hand-selected for New Zealand's most considered interiors.",
  categories: [
    {
      label: "American Oak",
      to: "/collections/timber/american-oak",
      image: "/assets/timber/american-oak-hero.jpg",
      caption: "Architectural panels & solid doors",
    },
    {
      label: "Timber Flooring",
      to: "/collections/timber/flooring",
      image: "/assets/timber/flooring-tile.jpg",
      caption: "Prime Oak · Restoration Plank · Herringbone",
    },
    {
      label: "Veneers",
      to: "/collections/timber/veneers",
      image: ASSETS.veneerShelves,
      caption: "Live Sawn · Rift Sawn · Light Character · Restoration",
    },
  ],
};

// ---- Architectural Panels (one-pager) ----
export const ARCH_PANELS = {
  eyebrow: "Timber Collection / Architectural Panels",
  title: "Architectural Panels",
  hero: IMG.timberGeo,
  intro: LOREM_LEAD,
  intro2: LOREM_BODY,
  products: Array.from({ length: 20 }).map((_, i) => ({
    id: `panel-${i + 1}`,
    name: `Architectural Panel ${String(i + 1).padStart(2, "0")}`,
    image: WOOD_POOL[i % WOOD_POOL.length],
    specs: [
      {
        title: "Standard Format",
        lengths: ["2400 mm", "2700 mm"],
        thicknesses: ["12 mm", "16 mm"],
      },
      {
        title: "Long Format",
        lengths: ["3000 mm", "3600 mm"],
        thicknesses: ["16 mm", "21 mm"],
      },
    ],
  })),
};

// ---- Timber Flooring hub ----
export const AMERICAN_OAK_HUB = {
  eyebrow: "Timber Collection / American Oak",
  title: "American Oak",
  hero: "/assets/timber/panels-hero.jpg",
  heroSlides: [
    "/assets/timber/panels-hero.jpg",
  ],
  intro:
    "American Oak is a timeless and versatile timber choice, valued for its natural warmth, distinctive grain and ability to complement a wide range of interior styles. Its clean appearance and subtle character make it suitable for both contemporary and traditional spaces, offering designers and architects the flexibility to create refined and cohesive interiors.",
  body:
    "With its natural variation in grain, texture and tone, every application of American Oak has its own individual character. It can be used to introduce warmth and texture into residential, commercial and architectural environments while maintaining a premium, understated finish. Explore the HOD American Oak collection to discover options designed to bring natural beauty, warmth and character into your next project.",
  categories: [
    { label: "Architectural Panels", to: "/collections/timber/architectural-panels", image: "/assets/timber/architectural-panels-tile.jpg", caption: "13 PrimeOak® profiles" },
    { label: "Solid Doors", to: "/collections/timber/solid-doors", image: "/assets/timber/solid-doors-tile.jpg", caption: "Made-to-order oak doors" },
  ],
};


export const FLOORING_HUB = {
  eyebrow: "Timber Collection / Timber Flooring",
  title: "Timber Flooring",
  hero: IMG.parquet,
  heroSlides: [
    "/assets/flooring-hero/fh1.webp",
    "/assets/flooring-hero/fh2.webp",
    "/assets/flooring-hero/fh3.webp",
    "/assets/flooring-hero/fh4.webp",
  ],
  intro:
    "Engineered oak flooring built to live for generations. Choose a format and finish, then make it yours with our curated colour range.",
  categories: [
    { label: "Prime Oak", to: "/collections/timber/flooring/prime-oak", image: "/assets/flooring/prime-oak.jpg", caption: "Three formats" },
    { label: "Restoration Plank", to: "/collections/timber/flooring/restoration-plank", image: "/assets/flooring/restoration-plank.webp", caption: "Two formats" },
    { label: "Herringbone", to: "/collections/timber/flooring/herringbone", image: "/assets/flooring/herringbone.jpg", caption: "Signature parquet" },
  ],
};

export const FLOORING = {
  "prime-oak": {
    name: "Prime Oak",
    hero: "/assets/flooring/prime-oak.jpg",
    intro: LOREM_LEAD,
    sizes: [
      "1900 × 190 × 14 mm",
      "2200 × 220 × 15 mm",
      "2400 × 260 × 20 mm",
    ],
    colours: pick(5, 0),
  },
  "restoration-plank": {
    name: "Restoration Plank",
    hero: "/assets/flooring/restoration-plank.webp",
    intro: LOREM_LEAD,
    sizes: ["2000 × 220 × 15 mm", "2400 × 260 × 20 mm"],
    colours: pick(5, 8),
  },
  herringbone: {
    name: "Herringbone",
    hero: "/assets/flooring/herringbone.jpg",
    intro: LOREM_LEAD,
    sizes: ["600 × 120 × 14 mm"],
    colours: pick(5, 14),
  },
};

export const FLOORING_ORDER = ["prime-oak", "restoration-plank", "herringbone"];

// ---- Veneers hub ----
export const VENEERS_HUB = {
  eyebrow: "Timber Collection / Veneers",
  title: "Veneers",
  hero: ASSETS.veneerShelves,
  intro:
    "Fine timber veneers for joinery, cabinetry and feature surfaces — supplied finished in an extensive colour range, or unfinished for full creative control.",
  categories: [
    { label: "Live Sawn", to: "/collections/timber/veneers/live-sawn", image: "/assets/veneers-new/live-sawn.jpg", caption: "Natural cathedral grain" },
    { label: "Rift Sawn", to: "/collections/timber/veneers/riff-sawn", image: "/assets/veneers-new/rift-sawn.jpg", caption: "Linear, consistent grain" },
    { label: "Light Character", to: "/collections/timber/veneers/light-character", image: "/assets/veneers-new/light-character.jpg", caption: "Subtle natural markings" },
    { label: "Restoration", to: "/collections/timber/veneers/restoration", image: "/assets/veneers-new/restoration.jpg", caption: "Aged, reclaimed character" },
  ],
};

const VENEER_BASE = {
  intro: LOREM_LEAD,
  sizes: ["2440 × 1220 mm", "3050 × 1220 mm"],
  finished: WOOD_TONES.slice(0, 27),
  unfinished: [{ name: "Raw / Unfinished", hex: "#d9cfba" }],
};

export const VENEERS = {
  "live-sawn": { name: "Live Sawn", hero: IMG.woodBoard2, ...VENEER_BASE, intro: "Live Sawn oak is cut straight through the log, preserving the full width of each board and revealing a bold, cathedral-like grain pattern. The result is a rich, dynamic surface with pronounced texture and natural movement — well suited to spaces that call for warmth and character." },
  "riff-sawn": { name: "Rift Sawn", hero: IMG.woodBoard3, ...VENEER_BASE, intro: "Rift Sawn oak is cut at a precise angle to produce a straight, linear grain with minimal flake and a clean, consistent appearance. Its refined, uniform texture makes it a natural fit for contemporary interiors seeking understated sophistication." },
  "light-character": { name: "Light Character", hero: IMG.woodBoard4, ...VENEER_BASE, intro: "Light Character oak offers a cleaner, more even grain with subtle natural markings and minimal knotting. Its soft, refined tone suits modern, minimalist spaces where a light, consistent finish is the priority." },
  restoration: { name: "Restoration", hero: ASSETS.veneerShelves, ...VENEER_BASE, intro: "Restoration oak carries the marks of time — subtle imperfections, weathering, and texture that speak to history and craftsmanship. Ideal for spaces looking to introduce warmth, depth, and a sense of story into their materials." },
};

export const VENEERS_ORDER = ["live-sawn", "riff-sawn", "light-character", "restoration"];

// ---- Mega-menu structure for the header dropdown ----
export const COLLECTION_MEGA = {
  columns: [
    { label: "Architectural Stone Collection", to: "/collections/natural-stone" },
    {
      label: "Custom Carpet Collection",
      to: "/collections/custom-carpet",
      groups: [
        { label: "Custom Carpet", to: "/collections/custom-carpet" },
        { label: "Carpet Tiles", to: "/collections/carpet-tiles" },
      ],
    },
    { label: "Wall Panel Collection", to: "/collections/hod-wall-panels" },
    { label: "Marble Slabs", to: "/collections/marble-slabs" },
    {
      label: "Timber Collection",
      to: "/collections/timber",
      groups: [
        {
          label: "American Oak",
          to: "/collections/timber/american-oak",
          items: [
            { label: "Architectural Panels", to: "/collections/timber/architectural-panels" },
            { label: "Solid Doors", to: "/collections/timber/solid-doors" },
          ],
        },
        {
          label: "Timber Flooring",
          to: "/collections/timber/flooring",
          items: [
            { label: "Prime Oak", to: "/collections/timber/flooring/prime-oak" },
            { label: "Restoration Plank", to: "/collections/timber/flooring/restoration-plank" },
            { label: "Herringbone", to: "/collections/timber/flooring/herringbone" },
          ],
        },
        {
          label: "Veneers",
          to: "/collections/timber/veneers",
          items: [
            { label: "Live Sawn", to: "/collections/timber/veneers/live-sawn" },
            { label: "Rift Sawn", to: "/collections/timber/veneers/riff-sawn" },
            { label: "Light Character", to: "/collections/timber/veneers/light-character" },
            { label: "Restoration", to: "/collections/timber/veneers/restoration" },
          ],
        },
      ],
    },
  ],
};
